/**
 * Admin — User operation snapshot page.
 *
 * Read-only audit trail of key user operations (login, trade, recharge, etc.).
 * Filterable by user_id and op_type.
 */
import { useState } from 'react';
import { Input, Select, Space, Tag, Modal, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { DataTable } from '../../components/DataTable';

const OP_TYPE_COLORS: Record<string, string> = {
  login: 'blue',
  trade: 'green',
  recharge: 'gold',
  withdraw: 'orange',
  kyc: 'purple',
  transfer: 'cyan',
  activation: 'magenta',
};

const OP_TYPE_OPTIONS = [
  { value: 'login', label: '登录' },
  { value: 'trade', label: '交易' },
  { value: 'recharge', label: '充值' },
  { value: 'withdraw', label: '提现' },
  { value: 'kyc', label: 'KYC' },
  { value: 'transfer', label: '转账' },
  { value: 'activation', label: '激活' },
];

interface UserOpSnapshot {
  id: string;
  user_id: number;
  op_type: string;
  summary: string;
  detail: unknown;
  ip: string | null;
  ua: string | null;
  create_time: string;
}

export function UserOpSnapshotPage() {
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(undefined);
  const [opTypeFilter, setOpTypeFilter] = useState<string | undefined>(undefined);
  const [detailModal, setDetailModal] = useState<UserOpSnapshot | null>(null);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80 },
    {
      title: '操作类型',
      dataIndex: 'op_type',
      key: 'op_type',
      width: 100,
      render: (v: string) => (
        <Tag color={OP_TYPE_COLORS[v] ?? 'default'}>{v}</Tag>
      ),
    },
    {
      title: '摘要',
      dataIndex: 'summary',
      key: 'summary',
      width: 260,
      ellipsis: true,
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: unknown, row: UserOpSnapshot) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setDetailModal(row)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 120 }}
          placeholder="用户ID"
          type="number"
          value={userIdFilter ?? ''}
          onChange={(e) => {
            const v = e.target.value ? Number(e.target.value) : undefined;
            setUserIdFilter(v);
          }}
          allowClear
        />
        <Select
          style={{ width: 140 }}
          placeholder="操作类型"
          allowClear
          value={opTypeFilter}
          onChange={(v) => setOpTypeFilter(v)}
          options={OP_TYPE_OPTIONS}
        />
      </Space>

      <DataTable<UserOpSnapshot>
        queryKey={['user-op-snapshot-list']}
        endpoint="/api/admin/user-op-snapshots"
        extraParams={{
          ...(userIdFilter ? { user_id: userIdFilter } : {}),
          ...(opTypeFilter ? { op_type: opTypeFilter } : {}),
        }}
        columns={columns}
        rowKey="id"
      />

      <Modal
        title={detailModal ? `操作详情 #${detailModal.id}` : ''}
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        width={640}
      >
        {detailModal && (
          <div>
            <p><strong>用户ID:</strong> {detailModal.user_id}</p>
            <p><strong>操作类型:</strong> <Tag color={OP_TYPE_COLORS[detailModal.op_type] ?? 'default'}>{detailModal.op_type}</Tag></p>
            <p><strong>摘要:</strong> {detailModal.summary}</p>
            <p><strong>IP:</strong> {detailModal.ip ?? '-'}</p>
            <p><strong>UA:</strong> {detailModal.ua ?? '-'}</p>
            <p><strong>时间:</strong> {new Date(detailModal.create_time).toLocaleString('zh-CN')}</p>
            {Boolean(detailModal.detail) && (
              <>
                <p><strong>详细数据:</strong></p>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, maxHeight: 300, overflow: 'auto', fontSize: 12 }}>
                  {JSON.stringify(detailModal.detail, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default UserOpSnapshotPage;
